// @flow

/*
   Util functions for traversing tree shaped node collections.
   This module just focuses on traversing up and down the node tree,
   remaining agnostic as to whether the nodes are editor nodes, task history
   nodes, etc. Other modules add editor specific behavior, etc.
*/

import _ from 'lodash';

import type {
  FlowNode,
  NodeMap,
  NodePredicate,
  SkinnyNode,
} from 'app/beta-editor/flowState';
import type { ID } from 'app/common/types';

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

/*
   Get the parent id of a node, respecting the fact that task history
   nodes use parent instead of parent_id.
*/
export const getParentId = (node?: FlowNode | SkinnyNode) =>
  (node || {}).parent_id || (node || {}).parent || null;

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

/*
   Return map of parent node ids to children ids.
   Note that paths group nodes can have multiple children.
   For example:

   {
     1: [2],
     2: [3],
     3: [4, 5]
     4: [6],
     5: [7],
   }
 */
export const getParentsToChildrenMap = (nodes: NodeMap | Array<FlowNode>) =>
  _.reduce(
    nodes,
    (map, node) => {
      const parentId = getParentId(node);
      if (parentId) {
        const children = map[parentId] || [];
        children.push(node.id);
        map[parentId] = children;
      }
      return map;
    },
    {}
  );

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

// internal helper function used by getNodeAndDescendants
const getNodes = (
  nodes,
  startNode,
  parentsToChildren,
  shouldStopFn: NodePredicate
): Array<FlowNode> => {
  const results = [startNode];

  const childIds = parentsToChildren[startNode.id];
  if (!shouldStopFn(startNode) && childIds) {
    const children = childIds.map(id => nodes[id]);
    return results.concat(
      _.flatten(
        _.map(children, child =>
          getNodes(nodes, child, parentsToChildren, shouldStopFn)
        )
      )
    );
  }

  return results;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

/*
   Return current node and descendants, sorted in step order.
   Starts with first node of zap if no startNodeId param.

   Stops descending at first node where shouldStopFn returns true
   (if supplied).
 */
export const getNodeAndDescendants = (
  nodes: NodeMap,
  startNodeId: ID,
  shouldStopFn?: NodePredicate = () => false
) => {
  const startNode = startNodeId
    ? nodes[startNodeId]
    : _.find(nodes, node => getParentId(node) === null);
  const parentsToChildren = getParentsToChildrenMap(nodes);
  return getNodes(nodes, startNode, parentsToChildren, shouldStopFn);
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const getRootNode = (nodes: NodeMap | Array<FlowNode>) =>
  _.find(nodes, node => getParentId(node) === null);

const toMap = nodes => {
  if (Array.isArray(nodes)) {
    return _.reduce(
      nodes,
      (map, node) => {
        map[node.id] = node;
        return map;
      },
      {}
    );
  }
  return nodes;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

/*
   Return list of nodes sorted in sort order, starting with root node
   of tree / root node of zap.
*/
export const sortNodes = (nodes: NodeMap | FlowNode[]) => {
  const rootNode = getRootNode(nodes);
  if (!rootNode) {
    return [];
  }
  return getNodeAndDescendants(toMap(nodes), rootNode.id);
};

/*
  Get descendants of a node within a path or top level of a zap.
*/
export const getDescendants = (nodes: NodeMap, nodeId: ID): FlowNode[] =>
  getNodeAndDescendants(nodes, nodeId).slice(1);

/*
   Returns an array of ancestors for the given node, sorted in order
   starting from the root node of the zap and working on down.
 */
export const getAncestors = (nodes: NodeMap, startNodeId: ID): FlowNode[] => {
  const startNode = nodes[startNodeId];
  const parentId = getParentId(startNode);
  if (parentId) {
    return getAncestors(nodes, parentId).concat(nodes[parentId]);
  }
  return [];
};

/*
   Return immediate children of node, excluding grandchildren, etc.
 */
export const getChildren = (nodes: NodeMap, nodeId: ID) => {
  const parentChildrenMap = getParentsToChildrenMap(nodes);
  const children = parentChildrenMap[nodeId] || [];
  return children.map(id => nodes[id]);
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

/*
   Find node or ancestor node that matches condition, starting
   with startNodeId and working up the ancestors tree.
*/
export const findNodeUpwards = (
  nodes: NodeMap,
  startNodeId: ID,
  isFoundFn: NodePredicate
) => {
  const startNode = nodes[startNodeId];
  const parentId = getParentId(startNode);
  if (!parentId || isFoundFn(startNode)) {
    return startNode;
  }
  const parent = nodes[parentId];
  return parent ? findNodeUpwards(nodes, parent.id, isFoundFn) : undefined;
};
